import React from 'react';

import {
  STATUS_TEXT,
  StatusCode,
  UTM_PARAM_KEYS,
  UTM_PARAM_VALUES,
} from '../../shared/constants';
import { IFormTemplate, IFormValues, IRawData } from '../../types/shared';

import { QueryParameters } from '../../tokens';
import ReactMarkdownExtended from '../generic/ReactMarkdownExtended';

export const escapeJsonForHtml = (jsonString: string): string => {
  return jsonString
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
};

export const transformItemsBasic = (items: IFormValues) => {
  const keys = Object.keys(items);

  return keys.reduce((total, key) => {
    const newItem = `${key}: ${items[key]}\n\n`;
    return `${total}${newItem}`;
  }, '');
};

const transformTemplateFormRawData = (template: IFormTemplate) => {
  const { content } = template;
  const keys = Object.keys(content);
  return keys.reduce((transformedTemplate, item) => {
    const newLine = `${content[item].content[0].value}\n\n`;
    return `${transformedTemplate}${newLine}`;
  }, '');
};

export const transformTemplate = (
  template: IRawData | string | null,
  items: IFormValues
) => {
  if (!template) {
    return transformItemsBasic(items);
  }
  let templatePlainText = template;
  if ((template as IRawData).raw) {
    templatePlainText = transformTemplateFormRawData(
      JSON.parse((template as IRawData).raw)
    );
  }

  const keys = Object.keys(items);
  return keys.reduce((totalTemplate, key) => {
    return (totalTemplate as string).replace(`{${key}}`, items[key] as string);
  }, templatePlainText) as string;
};

export const capitalizeString = (input: string) => {
  if (!input) {
    return '';
  }
  const transformedInput = input.split('-');
  return transformedInput.reduce((total, value) => {
    return (
      total + value.toLowerCase().replace(/\b(\w)/g, (s) => s.toUpperCase())
    );
  }, '');
};

export const getDescription = (description: string, items?: IFormValues) => {
  return (
    <ReactMarkdownExtended>
      {transformTemplate(description as string, items || {})}
    </ReactMarkdownExtended>
  );
};

export const sanitize = (text: string) =>
  text.replace(/[<>]/g, '').replace(/\n/g, ' ').trim();

export const parseSectionID = (id: string, title?: string) =>
  !title ? id : `${id}__${title.toLowerCase().split(' ').join('-')}`;

export const isExternalUrl = (url?: string) => {
  if (!url) return false;

  const pattern = /^(http.+)$/;
  const string = new RegExp(pattern);

  return !!url.match(string);
};

export const isVideoUrl = (url?: string) =>
  url?.includes('videos.ctfassets.net');

// In this case, only when the url contains the nlgroeit query parameter (tpci id) remove it.
export const trimUrlQueryParams = (url: string): string => {
  if (url.includes(`?${QueryParameters.TPCI_NLGROEIT}`)) {
    return url.split('?')[0];
  }

  return url;
};

export const parseJsonForTracking = (filters: string) => {
  const addPipeSeparation = filters.replace(/},{/g, '|');
  const stripRemaingCharacters = addPipeSeparation.replace(/[\])}[{("]/g, '');
  return stripRemaingCharacters;
};

export const setMessageBasedOnCode = (code?: number) => {
  switch (code) {
    case StatusCode.BAD_REQUEST:
      return STATUS_TEXT.BAD_REQUEST;
    case StatusCode.FORBIDDEN:
      return STATUS_TEXT.FORBIDDEN;
    case StatusCode.NOT_FOUND:
      return STATUS_TEXT.NOT_FOUND;
    case StatusCode.UNAUTHORIZED:
      return STATUS_TEXT.UNAUTHORIZED;
    case StatusCode.SERVER_ERROR:
      return STATUS_TEXT.SERVER_ERROR;
    case StatusCode.GATEWAY_TIMEOUT:
      return STATUS_TEXT.GATEWAY_TIMEOUT;
    case StatusCode.REQUEST_TIMEOUT:
      return STATUS_TEXT.REQUEST_TIMEOUT;
    default:
      return STATUS_TEXT.ERROR_FALLBACK;
  }
};

export const trackFormattedQuestion = (
  questionCopy: string,
  userAnswer: string
) => {
  return `${questionCopy} | ${userAnswer}`.toLowerCase();
};

export const createUtmParam = (paramKey: string, paramValue: string) => {
  return `${paramKey}=${paramValue}`;
};

export const replaceUtmParam = (url: string, newParam: string) => {
  return url.replace(
    `${UTM_PARAM_KEYS.campaign}=${UTM_PARAM_VALUES.dkpn}`,
    newParam
  );
};

export const getDutchFormattedDateTime = () => {
  const date = new Date();

  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Amsterdam',
  };

  const formatter = new Intl.DateTimeFormat('nl-NL', options);

  return formatter.format(date);
};

export const capString = (value: unknown, maxLength: number): unknown => {
  if (typeof value === 'string') {
    return value.slice(0, maxLength);
  }
  return value;
};

export const extractFirstLineFromMarkdown = (
  markdownContent: string
): string => {
  if (!markdownContent) return '';

  const firstLine = markdownContent.split('\n')[0];

  // Remove common markdown syntax from the beginning of the line
  return firstLine
    .replace(/^#{1,6}\s*/, '') // Remove heading syntax (# ## ### etc.)
    .replace(/^\*{1,2}\s*/, '') // Remove bold/italic asterisks
    .replace(/^_{1,2}\s*/, '') // Remove bold/italic underscores
    .replace(/^-\s*/, '') // Remove list dash
    .replace(/^\*\s*/, '') // Remove list asterisk
    .replace(/^\d+\.\s*/, '') // Remove numbered list
    .replace(/^>\s*/, '') // Remove blockquote
    .trim();
};
